import React, { useEffect, useState } from "react";
import {
  IconButton,
  Typography,
  Tooltip,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { Clear } from "@mui/icons-material";
import { PopoverListButtons } from "../../../Utils/styledComps";

const API_KEY = "51957820-01456caeccfa4cd39bc50642c"; // 🔑 API key
const BASE_URL = "https://pixabay.com/api/";

// categories definition
const categories = [
  { label: "Photos", type: "photo", q: "nature" },
  { label: "Graphics", type: "illustration", q: "arrow" },
  { label: "Stickers", type: "vector", q: "sticker" },
  { label: "Shapes", type: "vector", q: "shape" },
  { label: "Charts", type: "vector", q: "chart" },
];

// fetch helper
async function fetchImages(type, q, perPage = 20, page = 1) {
  const res = await fetch(
    `${BASE_URL}?key=${API_KEY}&image_type=${type}&q=${encodeURIComponent(
      q
    )}&per_page=${perPage}&page=${page}`
  );
  return res.json();
}

const PixabayImageLibrary = ({ handlePopoverClose, onSelectImage }) => {
  const [imagesByCategory, setImagesByCategory] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCategory, setModalCategory] = useState(null);
  const [modalImages, setModalImages] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [currentCat, setCurrentCat] = useState(null);

  useEffect(() => {
    async function loadInitial() {
      const results = {};
      for (let cat of categories) {
        const data = await fetchImages(cat.type, cat.q, 4);
        results[cat.label] = data.hits;
      }
      setImagesByCategory(results);
    }
    loadInitial();
  }, []);

  const handleSeeAll = async (cat) => {
    setPage(1);
    setCurrentCat(cat);
    const data = await fetchImages(cat.type, cat.q, 20, 1);
    setModalCategory(cat.label);
    setModalImages(data.hits);
    setHasMore(data.hits.length > 0);
    setModalOpen(true);
  };

  const loadMore = async () => {
    if (!hasMore || loading || !currentCat) return;
    setLoading(true);
    const nextPage = page + 1;
    const data = await fetchImages(currentCat.type, currentCat.q, 20, nextPage);
    setModalImages((prev) => [...prev, ...data.hits]);
    setPage(nextPage);
    setHasMore(data.hits.length > 0);
    setLoading(false);
  };

  // 👇 scroll listener
  const handleScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 50; // near bottom
    if (bottom) {
      loadMore();
    }
  };

  return (
    <>
      <div style={{ height: "500px", width: "315px", overflowY: "auto" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginRight: "10px",
          }}
        >
          <Typography
            sx={{
              m: 0,
              pl: 2,
              color: "#f0f2f7",
              fontSize: 18,
              fontWeight: 800,
            }}
          >
            Library
          </Typography>
          <IconButton
            edge="end"
            sx={{ borderRadius: 0 }}
            onClick={handlePopoverClose}
          >
            <Clear />
          </IconButton>
        </div>

        {/* Categories */}
        {categories.map((cat) => (
          <div key={cat.label} style={{ margin: "10px 0" }}>
            {/* title + see all */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0 10px",
              }}
            >
              <Typography
                sx={{
                  color: "#f0f2f7",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {cat.label}
              </Typography>
              <Button
                size="small"
                sx={{ color: "#90caf9", fontSize: 12 }}
                onClick={() => handleSeeAll(cat)}
              >
                See All
              </Button>
            </div>

            {/* 4 thumbnails */}
            <div
              style={{
                display: "flex",
                gap: 5,
                margin: "10px",
                flexWrap: "wrap",
              }}
            >
              {imagesByCategory[cat.label]?.map((img) => (
                <div
                  key={img.id}
                  style={{
                    width: "68px",
                    height: "68px",
                    cursor: "pointer",
                  }}
                  onClick={() => onSelectImage(img.largeImageURL)}
                >
                  <img
                    src={img.previewURL}
                    crossOrigin="anonymous"
                    alt={img.tags}
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: 6,
                      objectFit: "cover",
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Modal for see all */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { maxHeight: "calc(80% - 64px)" },
        }}
      >
        {/* Header with Title + Cross */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px 16px",
          }}
        >
          <Typography
            sx={{
              m: 0,
              fontSize: 18,
              fontWeight: 600,
            }}
          >
            {modalCategory}
          </Typography>
          <IconButton edge="end" onClick={() => setModalOpen(false)}>
            <Clear />
          </IconButton>
        </div>
        <DialogContent
          dividers
          onScroll={handleScroll}
          sx={{ overflowY: "auto" }}
        >
          <Grid container spacing={2}>
            {modalImages.map((img) => (
              <Grid item xs={3} key={img.id}>
                <img
                  src={img.previewURL}
                  crossOrigin="anonymous"
                  alt={img.tags}
                  style={{
                    width: "100%",
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    onSelectImage(img.largeImageURL);
                    setModalOpen(false);
                  }}
                />
              </Grid>
            ))}
          </Grid>
          {loading && (
            <Typography sx={{ mt: 2, textAlign: "center" }}>
              Loading more...
            </Typography>
          )}
          {!hasMore && (
            <Typography sx={{ mt: 2, textAlign: "center", color: "gray" }}>
              No more images
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PixabayImageLibrary;
